import { v4 as uuidv4 } from "uuid"

import { $t } from "@/plugins/i18n"
import { formatDate, getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"
import { replaceHtmlTags } from "@/utils/helpers"
import { syncDescriptionHtml } from "@/utils/syncDescriptionHtml"

import { Base } from "./Base"
import { StructuredDescription } from "./EditorData"
import { ReferencedResourcePreviews } from "./Search"
import { User } from "./User"

import type { ChangeItemByUser, CreationSource, ExternalResource, Item, ItemConfigs } from "./Item"
import type { TeamType } from "./Team"

export type ModalTypesForCratingTask =
  | "RE_OPEN"
  | "ASSIGN_TEAM"
  | "IN_PROGRESS"
  | "DEFINITION"
  | null

export interface ReturnTeamTask {
  /** A copy of the Item with the team it goes back to */
  item: Item
  task: Task
  taskTypes: TaskTypeOption[]
}

export type TotalOption = {
  label: string
  value: number
  id?: string
  isUnestimated?: boolean
  isOtherUnestimated?: boolean
}

export type TaskOptions = {
  id: string
  label: string
  icon?: object
  iconOption?: iconOption
}

export type iconOption = {
  width?: string
  height?: string
  color?: string
}

export class TaskTypeOption {
  id: TaskType
  label: string

  constructor(props: TaskTypeOption) {
    this.id = props.id
    this.label = props.label
  }
}

export const taskTypes = [
  "RESEARCH",
  "CONTENT",
  "GRAPHIC_DESIGN",
  "UI_UX",
  "SEO",
  "SMM",
  "BUG",
  "DEVOPS",
  "QA",
  "DISCOVERY",
  "DESIGN",
  "DEV",
  "PR_REVIEW",
  "OPS",
  "CI_CD_PIPELINE",
  "INFRASTRUCTURE",
  "SECURITY",
  "MITIGATION",
  "REMEDIATION",
  "TESTING",
  "REVIEW",
  "OBSERVABILITY",
  "PERFORMANCE",
  "COST_OPTIMIZATION",
  "ALERTING",
  "DOCUMENTATION",
  "UX_FLOW",
  "UI_UX_TESTING",
  "UI_DESIGN",
  "DELIVERY",
  "DEV_HANDOFF"
]

export const taskTypesOrder: { [key: string]: TaskType[] } = {
  DEVELOPMENT: ["DISCOVERY", "DESIGN", "UI_UX", "DEV", "BUG", "DEVOPS", "PR_REVIEW", "QA"],
  DEVOPS: [
    "DISCOVERY",
    "DEV",
    "BUG",
    "CI_CD_PIPELINE",
    "INFRASTRUCTURE",
    "SECURITY",
    "MITIGATION",
    "REMEDIATION",
    "TESTING",
    "REVIEW",
    "OBSERVABILITY",
    "PERFORMANCE",
    "COST_OPTIMIZATION",
    "ALERTING",
    "PR_REVIEW",
    "QA",
    "DOCUMENTATION"
  ],
  SECURITY: ["DISCOVERY", "MITIGATION", "REMEDIATION", "TESTING", "REVIEW", "DOCUMENTATION"],
  DESIGN: ["DISCOVERY", "DESIGN", "DOCUMENTATION"],
  UI_UX: ["DISCOVERY", "UX_FLOW", "UI_UX_TESTING", "UI_DESIGN", "DELIVERY", "DEV_HANDOFF"],
  MARKETING: [
    "RESEARCH",
    "CONTENT",
    "GRAPHIC_DESIGN",
    "DISCOVERY",
    "DESIGN",
    "UI_UX",
    "SEO",
    "SMM",
    "DEV",
    "BUG",
    "OPS",
    "DEVOPS",
    "PR_REVIEW",
    "QA",
    "DOCUMENTATION"
  ]
}

export const listOfSortedTaskTypes = [
  "DISCOVERY",
  "RESEARCH",
  "CONTENT",
  "GRAPHIC_DESIGN",
  "UI_UX",
  "SEO",
  "SMM",
  "DESIGN",
  "MITIGATION",
  "REMEDIATION",
  "TESTING",
  "REVIEW",
  "DEV",
  "BUG",
  "DEVOPS",
  "OPS",
  "PR_REVIEW",
  "QA",
  "CI_CD_PIPELINE",
  "INFRASTRUCTURE",
  "SECURITY",
  "OBSERVABILITY",
  "PERFORMANCE",
  "COST_OPTIMIZATION",
  "ALERTING",
  "DOCUMENTATION",
  "UX_FLOW",
  "UI_UX_TESTING",
  "UI_DESIGN",
  "DELIVERY",
  "DEV_HANDOFF"
] as const

export type TaskType = (typeof taskTypes)[number]

export interface ITask {
  name: string
  type: TaskType
  localId?: string
  description?: string
  fileMetadataIds?: string[]
  typeParsed?: TaskTypeOption
  estimatedHours?: number
  order?: number
  id?: string
  itemId?: string
  owner?: User
  ownerId?: string
  estimationModificationTime?: string
  outdatedEstimation?: boolean
  structuredDescription: StructuredDescription
  spec?: StructuredDescription
  referencedResourcePreviews?: ReferencedResourcePreviews
  checklistFullyChecked: boolean
  expectedDeliveryDate?: string
  readOnly?: boolean
  teamType?: TeamType | null
}

export interface UpdateTaskList {
  tasks: Task[]
  item: Item
  projectId?: string
}

/** Fields edited locally while an Item page was open, saved for draft restoration after reload. */
export interface TaskDraftEdit {
  taskId?: string
  localId: string
  fields: Partial<Task>
  fieldNames?: (keyof Task)[]
  isNew?: boolean
}

export interface UpdateTaskOrder {
  taskIdsAndOrder: TaskIdAndOrder[]
  itemId: string
  projectId?: string
}

export interface TaskIdAndEstimation {
  taskId: string
  estimation: number
}

export interface TaskIdAndOrder {
  taskId: string
  order: number
}

export interface PatchTaskList {
  taskIdsAndEstimation: TaskIdAndEstimation[]
  item: Item
  projectId?: string
}

export class Task extends Base<Task> implements ITask {
  id?: string
  localId: string
  name: string
  createdByUser?: ChangeItemByUser
  createdDate: string
  restoreTaskUrl: string
  type: TaskType
  typeParsed: TaskTypeOption
  estimatedHours?: number
  isTaskRestored?: boolean
  fileMetadataIds?: string[]
  order: number
  owner?: User
  ownerId?: string
  estimationModificationTime?: string
  itemId?: string // Potentialy unused prop
  outdatedEstimation?: boolean
  oldEstimation?: number
  structuredDescription: StructuredDescription
  spec?: StructuredDescription
  referencedResourcePreviews?: ReferencedResourcePreviews
  checklistFullyChecked: boolean
  expectedDeliveryDate?: string
  creationSource?: CreationSource
  externalResourceId?: ExternalResource
  readOnly: boolean
  teamType: TeamType | null

  constructor(props?: Partial<ITask>) {
    super()
    this.setDefaultData()

    if (!props) return

    this.set(props)
  }

  set(props: Partial<Task>) {
    this.id = props.id ?? this.id
    this.localId = props.localId ?? this.localId ?? uuidv4()
    this.isTaskRestored = props.isTaskRestored ?? undefined
    this.name = props.name ?? ""
    this.type = props.type ?? "DEV"
    this.typeParsed = Task.getParsedType(this.type)
    this.estimatedHours = props.estimatedHours ?? undefined
    this.oldEstimation = props.oldEstimation ?? undefined
    this.fileMetadataIds = props.fileMetadataIds ?? []
    this.order = props.order ?? 1
    this.owner = props.owner ? User.create(props.owner) : undefined
    this.ownerId = props.ownerId ?? ""
    this.estimationModificationTime = props.estimationModificationTime ?? ""
    this.outdatedEstimation = props.outdatedEstimation ?? false
    this.structuredDescription = new StructuredDescription(props.structuredDescription)
    this.structuredDescription.html = syncDescriptionHtml({
      referencedResourcePreviews: props.referencedResourcePreviews,
      structuredDescription: this.structuredDescription
    })
    this.spec = props.spec ? new StructuredDescription(props.spec) : undefined
    this.createdDate = props.createdDate ?? ""
    this.createdByUser = props.createdByUser
    this.checklistFullyChecked = props.checklistFullyChecked ?? true
    this.expectedDeliveryDate = props.expectedDeliveryDate ?? undefined
    this.creationSource = props.creationSource ?? null
    this.readOnly = props.readOnly ?? false
    this.teamType = props.teamType ?? null
    return this
  }

  setDefaultData() {
    this.id = ""
    this.localId = uuidv4()
    this.name = ""
    this.type = "DEV"
    this.typeParsed = Task.getParsedType(this.type)
    this.fileMetadataIds = []
    this.order = 1
    this.owner = undefined
    this.ownerId = ""
    this.estimationModificationTime = ""
    this.oldEstimation = undefined
    this.checklistFullyChecked = true
    this.structuredDescription = new StructuredDescription()
  }

  static create(props?: ITask): Task {
    return new Task(props)
  }

  static createDefault(
    { itemId, order }: { itemId?: string; order?: number } = {
      itemId: "",
      order: 1
    }
  ): Task {
    return new Task({
      name: "",
      type: "DEV",
      itemId,
      structuredDescription: new StructuredDescription(),
      order
    })
  }

  static putIndexes(tasks: Task[]) {
    return tasks.map((item, order) =>
      Object.assign(item, {
        order: order + 1
      })
    )
  }

  static getTotalValue({ type, tasks }: { type?: TaskType; tasks: Task[] }) {
    if (!type) {
      return tasks
        .filter(item => item.isEstimated)
        .reduce((acc, item) => acc + Number(item.estimatedHours), 0)
    }
    return tasks
      .filter(item => item.typeParsed && item.typeParsed.id === type && item.isEstimated)
      .reduce((acc, item) => acc + Number(item.estimatedHours), 0)
  }

  static getTaskTypes(tasks: Task[]) {
    return new Set(tasks.filter(task => task.isNotEmpty).map(task => task.typeParsed.id))
  }

  get simpleDiff() {
    return {
      name: this.name,
      typeParsed: this.typeParsed,
      estimatedHours: this.estimatedHours,
      order: this.order,
      owner: this.owner,
      structuredDescription: this.structuredDescription
    }
  }

  static getParsedType(type: TaskType): TaskTypeOption {
    return new TaskTypeOption({ id: type, label: $t(`task.types.${type.toLowerCase()}`) })
  }

  getJsonObj() {
    return {
      id: this.id,
      name: replaceHtmlTags(this.name),
      type: this.typeParsed.id,
      estimatedHours: this.estimatedHours,
      order: this.order,
      ownerId: this.owner?.id,
      fileMetadataIds: this.fileMetadataIds,
      estimationModificationTime: this.estimationModificationTime,
      outdatedEstimation: this.outdatedEstimation,
      structuredDescription: this.structuredDescription?.getJsonObj(),
      ...(this.spec ? { spec: new StructuredDescription(this.spec).getJsonObj() } : {}),
      expectedDeliveryDate: this.expectedDeliveryDate
    }
  }

  get isNotEmpty(): boolean {
    return !!this.name.trim()
  }

  get isEstimated() {
    return this.estimatedHours !== null && this.estimatedHours !== undefined
  }

  get status() {
    return this.oldEstimation !== null
      ? this.estimatedHours == 0
        ? "Done"
        : "In Progress"
      : "Added"
  }

  getReadOnlyHint(itemTeamType: TeamType, itemConfig: ItemConfigs) {
    if (!this.readOnly) return ""
    if (itemTeamType === "NONE") return $t("task.readOnlyDefaultHint", { type: this.typeString })
    const taskTeamType =
      this.teamType && this.teamType !== "NONE"
        ? this.teamType
        : this.getOnlyTeamType(itemTeamType, itemConfig)
    const teamType = $t(`teamTypes.${itemTeamType}`)
    return taskTeamType
      ? $t("task.readOnlyHint", { taskTeamType: $t(`teamTypes.${taskTeamType}`), teamType })
      : $t("task.readOnlyTypeHint", { type: this.typeString, teamType })
  }

  private getOnlyTeamType(itemTeamType: TeamType, itemConfig: ItemConfigs): TeamType | null {
    const teamTypes = (Object.keys(itemConfig) as TeamType[]).filter(
      teamType =>
        teamType !== "NONE" &&
        teamType !== itemTeamType &&
        itemConfig[teamType].configs.some(config => config.taskTypes.includes(this.type))
    )
    return teamTypes.length === 1 ? teamTypes[0] : null
  }

  get typeString() {
    return Task.getParsedType(this.type)?.label ?? ""
  }

  get ownerFullName() {
    return this.owner ? `@${this.owner.firstName} ${this.owner.lastName}` : "Unassigned"
  }

  get hasExpectedDeliveryDate() {
    return !!this.expectedDeliveryDate
  }

  get parsedExpectedDeliveryDate() {
    return this.expectedDeliveryDate ? toCalendarDate(this.expectedDeliveryDate) : null
  }

  get formattedExpectedDeliveryDate() {
    return this.formatExpectedDeliveryDate()
  }

  get isExpectedDeliveryDateOverdue() {
    if (!this.parsedExpectedDeliveryDate) return false
    return this.parsedExpectedDeliveryDate < getTodayCalendarDate()
  }

  formatExpectedDeliveryDate(dateFormat = "dd MMM yyyy", emptyValue = "") {
    if (!this.expectedDeliveryDate) return emptyValue
    return formatDate(this.expectedDeliveryDate, dateFormat, { dateOnly: true })
  }

  static getEstimatedTasksCount({ tasks, type }: { tasks: Task[]; type?: TaskType }) {
    const estimatedTasks = tasks.filter(
      e => e.estimatedHours !== null && e.estimatedHours !== undefined
    )

    const filteredTasks = type
      ? estimatedTasks.filter(e => e.typeParsed.id === type)
      : estimatedTasks

    return filteredTasks.reduce((prev, next) => prev + Number(next.estimatedHours), 0)
  }

  static getEstimatedTasksItemsCount({ tasks, type }: { tasks: Task[]; type?: TaskType }) {
    return tasks
      ?.filter(e => e.isNotEmpty)
      .filter(e => e.typeParsed.id === type)
      ?.filter(e => e.estimatedHours! > 0)?.length
  }

  static checkCompletionOfAllTasks(tasks: Task[]) {
    return tasks.every(e => e.estimatedHours === 0)
  }

  static getItemEstimation(tasks) {
    if (!tasks.length) return undefined
    if (Task.checkCompletionOfAllTasks(tasks)) return 0
    const estimatedTasks = tasks.filter(e => !!e.estimatedHours)
    if (!estimatedTasks.length) return undefined
    return estimatedTasks.reduce((prev, next) => prev + Number(next.estimatedHours), 0)
  }
}
